define((require) => {
    "use strict";

    const Class                                         = require("shared/util/Class");
    const {
        uniq, pluck, first, isEmpty, where, union
    }   = require("underscore");
    const GeneralFunctions                              = require("editor/util/GeneralFunctions");
    const GuidelineModel                                = require("ui/editor/guideline/GuidelineModel");
    const SnapUtils                                     = require("util/SnapUtils");
    const THRESHOLD                                     = 8;


    function getClosestValueFromOneListInAnother(arr, arr2) {
        const mappedArr = arr.map(getClosestValueFromArr(arr2));
        let index = 0;
        let smallestDiff = Infinity;

        for (let i = 0; i < arr.length; i++) {
            const diff = Math.abs(arr[i] - mappedArr[i]);
            if (diff < smallestDiff) {
                index = i;
                smallestDiff = diff;
            }
        }

        return {value: arr[index], newValue: mappedArr[index]};
    }

    // Assumes the array is sorted, and not very big.
    const getClosestValueFromArr = GeneralFunctions.curry((arr, n) => {
        let below = first(arr);
        let above = first(arr);

        for (let i = 0; above < n; i++) {
            if (arr[i] < n) {
                below = arr[i];
            } else {
                above = arr[i];
            }
        }

        return (above - n < n - below) ? above : below;
    });


    /**
     * GridSet
     */
    return Class.extend({

        vList: null,    // Contains a list of vertical coordinates
        hList: null,    // Contains a list of horizontal coordinates
        vSet: null,     // Contains the horizontal points of each vertical line
        hSet: null,     // Contains the vertical points of each horizontal line
        sceneSize: null,

        constructor(sceneSize) {
            this.sceneSize = sceneSize;

            // Some variables for setting up the gridset.
            const sceneHeight = sceneSize.height;
            const sceneHeight2 = Math.round(sceneHeight / 2);
            const sceneWidth = sceneSize.width;
            const sceneWidth2 = Math.round(sceneWidth / 2);

            // The lists container just the raw snapline coordinates. They
            // shoud always be uniq and sorted. They are used for lookups.
            this.vList = [0, sceneWidth2, sceneWidth];
            this.hList = [0, sceneHeight2, sceneHeight];

            // The sets contains the min and max coordinate
            // belonging to each snapline coordinate
            this.vSet = {"_0": [0, sceneWidth]};
            this.hSet = {"_0": [0, sceneWidth]};

            this.vSet[`_${sceneWidth2}`] = [sceneHeight2];
            this.vSet[`_${sceneWidth}`] = [0, sceneHeight];
            this.hSet[`_${sceneHeight2}`] = [sceneWidth2];
            this.hSet[`_${sceneHeight}`] = [0, sceneWidth];
        },

        // Takes an element and adds its coordinates to each of the two sets
        addElement(el) {
            const newCoords = this._getGridCoords(el);

            this._addSet(this.vSet, newCoords.vCoords);
            this._addSet(this.hSet, newCoords.hCoords);

            this._addCoordinateToList(this.vList, newCoords.vCoords);
            this._addCoordinateToList(this.hList, newCoords.hCoords);

            this._removeDuplicatesAndSortTheLists();
        },

        /**
         * @param {GuidelineCollection} guidelines
         */
        addGuidelines(guidelines) {
            guidelines.each(function(guideline) {
                this._addGuideline(guideline);
            }, this);

            this._removeDuplicatesAndSortTheLists();
        },

        /**
         * @param {GuidelineModel} guideline
         */
        _addGuideline(guideline) {
            switch (guideline.get("direction")) {
                case GuidelineModel.HORIZONTAL:
                {
                    const horizontalCoordinates = [
                        [guideline.get("position"), [0, this.sceneSize.width]]
                    ];
                    this._addSet(this.hSet, horizontalCoordinates);
                    this._addCoordinateToList(this.hList, horizontalCoordinates);
                    break;
                }

                case GuidelineModel.VERTICAL:
                {
                    const verticalCoordinates = [
                        [guideline.get("position"), [0, this.sceneSize.height]]
                    ];
                    this._addSet(this.vSet, verticalCoordinates);
                    this._addCoordinateToList(this.vList, verticalCoordinates);
                    break;
                }

                default:
                    throw new Error(`'${guideline.get("direction")}' is not a supported direction.`);
            }
        },

        _removeDuplicatesAndSortTheLists() {
            this.vList = GeneralFunctions.sortNumbers(uniq(this.vList));
            this.hList = GeneralFunctions.sortNumbers(uniq(this.hList));
        },

        // Takes a set and some new coordinates (made with `getGridSet`) and adds them to the set
        _addSet(set, coordinates) {
            coordinates.forEach((coordinate) => {
                const key       = `_${coordinate[0]}`;
                const val       = coordinate[1];
                const coords    = union(set[key] || [], val);
                const minCoord  = Math.min(...coords);
                const maxCoord  = Math.max(...coords);

                set[key]      = uniq([minCoord, maxCoord]);
            });
        },

        _addCoordinateToList(list, coordinates) {
            coordinates.forEach((coordinate) => {
                list.push(coordinate[0]);
            });
        },

        // Gets a set of coordinates to determine to what point to draw a snapline
        // Example: getSet("vertical", 75);
        getSet(axis, coordinate) {
            const set = (axis === "vertical") ? this.vSet : this.hSet;
            const key = `_${coordinate}`;

            return set[key] || null;
        },

        _getSnapCoordinatesFromCornerCoordinates(cornerCoordinates) {
            const xs      = cornerCoordinates.map((cornerCoordinate) => cornerCoordinate.x); // 4 x-coords for the element
            const ys      = cornerCoordinates.map((cornerCoordinate) => cornerCoordinate.y); // 4 y-coords for the element

            // vertical gridlines
            const minX    = Math.min(...xs);                // left
            const maxX    = Math.max(...xs);                // right
            const cenX    = Math.round((minX + maxX) / 2);  // center x

            // horizontal gridlines
            const minY    = Math.min(...ys);                // top
            const maxY    = Math.max(...ys);                // bottom
            const cenY    = Math.round((minY + maxY) / 2);  // center y

            return {
                x: [minX, cenX, maxX],
                y: [minY, cenY, maxY]
            };
        },

        _getRoundedCornerCoordinates(transformable) {
            const coordinates = transformable.getCornerCoordinates() || [];

            return coordinates.map(GeneralFunctions.mapOverObj(Math.round));
        },

        // + transformable -> {snap coordinates}
        _getSnapCoordinatesFromTransformable(transformable) {
            return this._getSnapCoordinatesFromCornerCoordinates(this._getRoundedCornerCoordinates(transformable));
        },

        // + element -> {[set], [set]}
        _getGridCoords(el) {
            const cornerCoordinates   = SnapUtils.getRoundedCornerCoordinatesFromDom(el);
            const snapCoords          = this._getSnapCoordinatesFromCornerCoordinates(cornerCoordinates);

            // vertical Y coordinates
            const minXYs  = pluck(where(cornerCoordinates, {x: snapCoords.x[0]}), "y"); // y-coords that touch the left
            const maxXYs  = pluck(where(cornerCoordinates, {x: snapCoords.x[2]}), "y"); // y-coords that touch the right
            const cenXYs  = [snapCoords.y[1]];                                          // y-coord for the center

            // horizontal X coordinates
            const minYXs  = pluck(where(cornerCoordinates, {y: snapCoords.y[0]}), "x"); // x-coords that touch the top
            const maxYXs  = pluck(where(cornerCoordinates, {y: snapCoords.y[2]}), "x"); // x-coords that touch the bottom
            const cenYXs  = [snapCoords.x[1]];                                          // x-coord for the center

            const vCoords = [
                [snapCoords.x[0], minXYs],
                [snapCoords.x[1], cenXYs],
                [snapCoords.x[2], maxXYs]
            ];

            const hCoords = [
                [snapCoords.y[0], minYXs],
                [snapCoords.y[1], cenYXs],
                [snapCoords.y[2], maxYXs]
            ];

            return {
                vCoords,
                hCoords
            };
        },

        getOffset(transformable) {
            const snapCoords  = this._getSnapCoordinatesFromTransformable(transformable);
            const newXCoords  = getClosestValueFromOneListInAnother(snapCoords.x, this.vList);
            const newYCoords  = getClosestValueFromOneListInAnother(snapCoords.y, this.hList);
            const xOffset     = newXCoords.newValue - newXCoords.value;
            const yOffset     = newYCoords.newValue - newYCoords.value;
            const result      = {};

            if (Math.abs(xOffset) < THRESHOLD) result.x = {offset: xOffset, value: newXCoords.value};
            if (Math.abs(yOffset) < THRESHOLD) result.y = {offset: yOffset, value: newYCoords.value};

            return isEmpty(result) ? false : result;
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/services/snap/Gridsets.js